import { useContext, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import Button from "@mui/material/Button";
import MalnutritionHeader from "./malnutrition-header";
import MalnutritionDischargeBanner from "./malnutrition-discharge-banner";
import WhyFlaggedSection from "./why-flagged";
import SupportingDataSection from "./supporting-data";
import CriteriaFeedbackPanel from "./criteria-feedback-panel";
import NotificationToast from "../notification-toast";
import MalnutritionAPIContext from "../../contexts/MalnutritionAPIContext";
import { useMalnutritionFeedbackMutation } from "../../api/malnutrition/queries";
import CollapsibleSection from "../shared/collapsible-section";

import type { FC, ReactNode } from "react";
import type {
  MalnutritionFeedbackVariables,
  MalnutritionRiskAssessment,
  PatientWeightHistory,
} from "../../types/Malnutrition";
import { common, grey, red, green, deepPurple } from "@mui/material/colors";
import { BRAND_PURPLE, QVENTUS_PURPLE } from "./malnutrition-colors";

const CRITERIA_TOAST_SUCCESS = "Feedback submitted. Thank you.";
// Phrased to match the Yes/No control's inline error above it.
const CRITERIA_TOAST_ERROR = "Couldn’t submit feedback. Please try again.";

interface CriteriaToast {
  message: string;
  isError: boolean;
}

const styles = {
  pageBg: css`
    background: ${deepPurple[50]};
    min-height: 100vh;
  `,
  feedbackCard: css`
    background: ${common.white};
    margin: 0.75rem;
    padding: 0.75rem 0.875rem;
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  `,
  // The Qventus mark, not a palette colour — a round brand badge marking the
  // card as Qventus-generated, the same meaning the purple quote rules carry.
  qBadge: css`
    margin-right: 0.5rem;
    background: ${BRAND_PURPLE};
    color: ${common.white};
    border-radius: 50%;
    width: 1.125rem;
    height: 1.125rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
  `,
  feedbackPromptRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  `,
};

const sxStyles = {
  feedbackTitle: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: QVENTUS_PURPLE,
  },
  feedbackPrompt: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: grey[800],
  },
  feedbackButtonBase: {
    textTransform: "none",
    borderRadius: "1.25rem",
    minWidth: "3rem",
    height: "1.875rem",
    fontWeight: 600,
  },
  feedbackError: {
    fontSize: "0.75rem",
    color: red[700],
  },
};

// Both answers stay outlined pills; the selected one is tinted per the design —
// green for "Yes", red for "No" — rather than a filled variant. Deliberately
// kept through the ENG-30844 recolour: this is an affirmation, a separate axis
// from the card's "red means clinical decline" rule, and Michael confirmed it
// should stay. Don't neutralise it again.
const feedbackButtonSx = (selected: boolean, tone: "yes" | "no") => {
  if (!selected) {
    return {
      ...sxStyles.feedbackButtonBase,
      color: grey[800],
      borderColor: grey[400],
      "&:hover": { borderColor: grey[600], backgroundColor: grey[50] },
    };
  }
  const palette = tone === "yes" ? green : red;
  return {
    ...sxStyles.feedbackButtonBase,
    color: palette[700],
    borderColor: palette[600],
    backgroundColor: palette[50],
    "&:hover": { borderColor: palette[700], backgroundColor: palette[100] },
  };
};

interface FeedbackCardProps {
  // The whole assessment: the card needs the recorded answer plus, for the
  // criteria panel, the criteria the user is looking at.
  assessment: MalnutritionRiskAssessment;
}

const PatientFeedbackCard: FC<FeedbackCardProps> = ({ assessment }) => {
  const { feedback } = assessment;
  const { emrPatientID, emrHospitalID } = useContext(MalnutritionAPIContext);
  // One mutation for both steps. Owned here rather than inside the panel so a
  // submit landing after the panel closes still reports its outcome.
  const feedbackMutation = useMalnutritionFeedbackMutation();

  // The selected pill is the server's read-back, except while a save is in
  // flight, when it shows the vote being saved. The server's answer is never
  // copied into local state: a copy only re-syncs when the read-back *changes*,
  // so a save whose read-back came back null anyway (a new assessment version
  // landed around it) left the card showing a vote the server had no record of.
  // pendingVote holds only the in-flight intent, set on click and cleared when
  // the save settles — which, because the hook's onSuccess returns the
  // invalidation, is after the refetched read-back has landed. A failed save
  // falls back to the read-back. The buttons never lock between saves.
  const storedVote: "yes" | "no" | null = feedback
    ? feedback.is_good_candidate
      ? "yes"
      : "no"
    : null;
  const [pendingVote, setPendingVote] = useState<"yes" | "no" | null>(null);
  const shownVote = pendingVote ?? storedVote;
  // pendingVote is set synchronously, so a quick second click can't slip in
  // before the mutation reports loading; isLoading covers the panel's Submit,
  // whose callbacks a click's mutate() would drop.
  const saving = pendingVote !== null || feedbackMutation.isLoading;
  // The click's failure only — a failed Submit, the one step that carries the
  // follow-up, reports through its toast.
  const showError =
    feedbackMutation.isError &&
    feedbackMutation.variables?.payload.presented_criteria === undefined;
  // The panel's open state is stored as *which patient it was opened for*
  // rather than a bare boolean. Switching patients then closes it during
  // render, so there's never even a frame of one patient's criteria under
  // another's card — an effect would close it a frame late.
  const [openForPatientID, setOpenForPatientID] = useState<string | null>(null);
  const [toast, setToast] = useState<CriteriaToast | null>(null);
  // The panel is a child, so the focus handoff on close stays inside this
  // component rather than reaching across siblings.
  const noButtonRef = useRef<HTMLButtonElement | null>(null);

  const panelOpen = openForPatientID === emrPatientID;

  // The risk query polls, and the read-back is per assessment version: when a
  // new assessment lands, feedback comes back null so the card asks again (see
  // get_feedback in backend/api/serializers.py). The collapse's defaultOpen only
  // applies on mount, so a card collapsed over the old answer would stay
  // collapsed over the new question. Remount it when the answer disappears —
  // but not when one appears, so answering never yanks the prompt away.
  const answered = !!feedback;
  const [askAgain, setAskAgain] = useState(0);
  const wasAnswered = useRef(answered);
  const { reset: resetFeedbackMutation } = feedbackMutation;
  useEffect(() => {
    if (wasAnswered.current && !answered) {
      setAskAgain((n) => n + 1);
      // Clear what belonged to the previous question: a failed save's error,
      // the pending vote, and an open follow-up, whose checklist and seed were
      // the previous assessment's. None of it waits on a save in flight, which
      // reset() doesn't cancel: it still lands, stamped with whichever
      // assessment is current when it arrives (the accepted save-time stamp in
      // PatientMalnutritionFeedbackViewSet.create).
      setPendingVote(null);
      setOpenForPatientID(null);
      resetFeedbackMutation();
    }
    wasAnswered.current = answered;
  }, [answered, resetFeedbackMutation]);

  // Returning to a patient must not reopen the panel on its own: the state says
  // "open for X", so without this, navigating away from X and back would put an
  // empty panel in front of someone who never asked for it.
  useEffect(() => {
    setOpenForPatientID((openFor) =>
      openFor === null || openFor === emrPatientID ? openFor : null
    );
  }, [emrPatientID]);

  const closePanel = () => {
    setOpenForPatientID(null);
    noButtonRef.current?.focus();
  };

  const handleVote = (vote: "yes" | "no") => () => {
    if (saving) return;
    setPendingVote(vote);
    // "No" opens the follow-up, including when No is already the recorded
    // answer — that is the path to edit a reason already on record. "Yes"
    // closes it, and clears the reason server-side. A failed No leaves it
    // open: its Submit resends the No.
    setOpenForPatientID(vote === "no" ? emrPatientID : null);
    feedbackMutation.mutate(
      {
        emrPatientID,
        emrHospitalID,
        payload: { is_good_candidate: vote === "yes" },
      },
      { onSettled: () => setPendingVote(null) }
    );
  };

  // Resolves once the request settles either way, so the panel can drop its
  // pending state; the outcome itself is handled here.
  const handleCriteriaSubmit = (
    variables: MalnutritionFeedbackVariables
  ): Promise<void> =>
    new Promise((resolve) => {
      feedbackMutation.mutate(variables, {
        onSuccess: () => {
          closePanel();
          setToast({ message: CRITERIA_TOAST_SUCCESS, isError: false });
          resolve();
        },
        onError: () => {
          // Stays open with the input intact so they can retry.
          setToast({ message: CRITERIA_TOAST_ERROR, isError: true });
          resolve();
        },
      });
    });

  return (
    <>
      {/* Same collapse the Why Flagged / Supporting Data sections use, so the
        chevron and its keyboard/aria behaviour stay consistent across the
        card. */}
      <CollapsibleSection
        key={askAgain}
        // Already answered -> start collapsed; the question is done and the
        // card is just taking up room above Why Flagged. Only the initial
        // state, so answering now doesn't yank the prompt away mid-interaction
        // (and a failed save keeps its error visible).
        defaultOpen={!answered}
        wrapBodyInGrid={false}
        title="Help us improve"
        // span, not a div: CollapsibleSection renders the icon inside the
        // heading <Typography>, which is a <p>.
        icon={
          <Box component="span" className={styles.qBadge} aria-hidden>
            Q
          </Box>
        }
        headingTextSx={sxStyles.feedbackTitle}
        headingSx={{ marginTop: 0, marginBottom: 0 }}
        outerClassName={styles.feedbackCard}
        dataTestId="malnutrition-feedback"
      >
        <Box className={styles.feedbackPromptRow}>
          <Typography sx={sxStyles.feedbackPrompt}>
            Good candidate for assessment?
          </Typography>
          <Box sx={{ display: "flex", gap: "0.5rem" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleVote("yes")}
              aria-pressed={shownVote === "yes"}
              sx={feedbackButtonSx(shownVote === "yes", "yes")}
              disabled={saving}
            >
              Yes
            </Button>
            <Button
              ref={noButtonRef}
              variant="outlined"
              size="small"
              onClick={handleVote("no")}
              aria-pressed={shownVote === "no"}
              sx={feedbackButtonSx(shownVote === "no", "no")}
              disabled={saving}
            >
              No
            </Button>
          </Box>
        </Box>
        {showError && (
          <Typography sx={sxStyles.feedbackError}>
            Couldn’t save your feedback. Please try again.
          </Typography>
        )}
      </CollapsibleSection>
      {/* Its own card, between "Help us improve" and Why Flagged — which is
        pushed down, never hidden or reordered. Outside the collapse, so folding
        the card away doesn't unmount a half-typed answer. Keyed by patient so
        nothing survives a switch. */}
      {panelOpen && (
        <CriteriaFeedbackPanel
          key={emrPatientID}
          emrPatientID={emrPatientID}
          emrHospitalID={emrHospitalID}
          criteria={assessment.criteria_met}
          status={assessment.status}
          feedback={feedback}
          onSubmit={handleCriteriaSubmit}
          onSkip={closePanel}
          saveInFlight={feedbackMutation.isLoading}
        />
      )}
      {/* Hosted above the panel so it survives the panel unmounting, and single
        so a second submission replaces the toast rather than stacking one. */}
      <NotificationToast
        message={toast?.message}
        showToast={toast !== null}
        isError={toast?.isError ?? false}
        onClose={() => setToast(null)}
      />
    </>
  );
};

interface MalnutritionSectionsProps {
  // Both are independent and either can be absent: the assessment endpoint
  // returns null when a patient isn't flagged, and recent-weights can exist
  // (or not) regardless of the assessment.
  assessment: MalnutritionRiskAssessment | null;
  weights: PatientWeightHistory | null;
  onClose?: () => void;
  onExpand?: () => void;
  closeButtonCmpt?: ReactNode;
}

// Rendered per patient. Without the key the card is reused when the user
// switches to a patient whose assessment is already cached (a first visit
// shows the loader and remounts anyway), and a save still in flight for the
// previous patient would settle into this one — restoring that patient's vote,
// raising its error, closing this one's panel. Remounting drops those callbacks
// (react-query only notifies a mounted observer) and the card's state with them.
const FeedbackCard: FC<FeedbackCardProps> = (props) => {
  const { emrPatientID } = useContext(MalnutritionAPIContext);
  return <PatientFeedbackCard key={emrPatientID} {...props} />;
};

const MalnutritionSections: FC<MalnutritionSectionsProps> = ({
  assessment,
  weights,
  onClose,
  onExpand,
  closeButtonCmpt,
}) => (
  <Box className={styles.pageBg}>
    {assessment && (
      <>
        <MalnutritionHeader
          patient={assessment.patient}
          status={assessment.status}
          onClose={onClose}
          onExpand={onExpand}
          closeButtonCmpt={closeButtonCmpt}
        />
        <>
          <MalnutritionDischargeBanner edd={assessment.patient.edd} />
          {/* Feedback shows for every status (and null) except "following". When
              it's hidden, keep its top spacing so the section below the chips
              isn't cramped. */}
          {assessment.status !== "following" ? (
            <FeedbackCard assessment={assessment} />
          ) : (
            <Box sx={{ height: "0.75rem" }} />
          )}
          {assessment.status && (
            <WhyFlaggedSection
              criteria={assessment.criteria_met}
              status={assessment.status}
            />
          )}
        </>
      </>
    )}
    {weights && <SupportingDataSection weights={weights} />}
  </Box>
);

export default MalnutritionSections;
